import React, { useEffect } from 'react';
import '@mdi/font/css/materialdesignicons.min.css'; // Load Material Design Icons font
import { Platform, View } from 'react-native';
import { Provider } from 'react-redux';
import { PersistGate } from 'redux-persist/integration/react';
import { SafeAreaProvider } from 'react-native-safe-area-context';
import { NavigationContainer, DefaultTheme } from '@react-navigation/native';
import type { NavigatorScreenParams } from '@react-navigation/native';
import { useSelector, useDispatch } from 'react-redux';
import { store, persistor } from './src/store';
import { RootState, AppDispatch } from './src/store';
import { ReviewSearch } from './src/components/ReviewSearch';
import { Provider as PaperProvider } from 'react-native-paper';
import { theme } from './src/constants/theme';
import MdiIconWeb from './src/components/MdiIconWeb';
import AppNavigator from './src/navigation/AppNavigator';
import LoginScreen from './src/screens/auth/LoginScreen.web';
import InvitationRegisterScreen from './src/screens/auth/InvitationRegisterScreen.web';
import ForgotPasswordScreen from './src/screens/auth/ForgotPasswordScreen.web';
import ResetPasswordScreen from './src/screens/auth/ResetPasswordScreen.web';
import SupportScreen from './src/screens/SupportScreen';
import { api } from './src/services/api';

type TrackedRoute = {
  name: string;
  params?: {
    token?: unknown;
  };
};

type WebReviewStackParamList = {
  ReviewList: undefined;
  ReviewDetail: { reviewId: number };
  ItemDetail: { reviewId: number; itemId: number };
};

type WebResultsStackParamList = {
  ResultsList: undefined;
  ResultDetail: { reviewId: number };
  ItemDetail: { reviewId: number; itemId: number };
};

type WebRootParamList = {
  Home: undefined;
  Reviews: NavigatorScreenParams<WebReviewStackParamList> | undefined;
  Results: NavigatorScreenParams<WebResultsStackParamList> | undefined;
  Profile: undefined;
  Admin: undefined;
  InvitationRegister: { token: string };
  ForgotPassword: undefined;
  ResetPassword: { token: string };
  Support: undefined;
};

// Custom theme with proper fonts for web
const webTheme = {
  ...DefaultTheme,
  fonts: {
    regular: {
      fontFamily: Platform.OS === 'web' ? 'system-ui, -apple-system, sans-serif' : 'System',
      fontWeight: '400',
    },
    medium: {
      fontFamily: Platform.OS === 'web' ? 'system-ui, -apple-system, sans-serif' : 'System',
      fontWeight: '500',
    },
    bold: {
      fontFamily: Platform.OS === 'web' ? 'system-ui, -apple-system, sans-serif' : 'System',
      fontWeight: 'bold',
    },
    heavy: {
      fontFamily: Platform.OS === 'web' ? 'system-ui, -apple-system, sans-serif' : 'System',
      fontWeight: '900',
    },
  },
};


const AppContent = () => {
  const isAuthenticated = useSelector((state: RootState) => state.auth.isAuthenticated);
  const dispatch = useDispatch<AppDispatch>();
  const [currentRoute, setCurrentRoute] = React.useState<TrackedRoute | null>(null);
  
  
  useEffect(() => {
    // Force logout if no token in API service
    if (isAuthenticated && !api.token) {
      dispatch({ type: 'logout' });
    }
  }, [isAuthenticated, dispatch]);

  // Check if current route is an invitation route
  // Also check URL directly on initial load for invitation pattern
  const checkInvitationRoute = () => {
    if (currentRoute?.name === 'InvitationRegister' && currentRoute?.params?.token) {
      return true;
    }
    
    // Also check browser URL for invitation pattern
    if (Platform.OS === 'web' && typeof window !== 'undefined') {
      const urlPath = window.location.pathname;
      const inviteMatch = urlPath.match(/\/invite\/(.+)/);
      if (inviteMatch) {
        return true;
      }
    }
    
    return false;
  };
  
  // Check if current route is forgot password route
  const checkForgotPasswordRoute = () => {
    if (currentRoute?.name === 'ForgotPassword') {
      return true;
    }
    
    // Also check browser URL for forgot password pattern
    if (Platform.OS === 'web' && typeof window !== 'undefined') {
      const urlPath = window.location.pathname;
      if (urlPath === '/forgot-password') {
        return true;
      }
    }
    
    return false;
  };
  
  // Check if current route is reset password route
  const checkResetPasswordRoute = () => {
    if (currentRoute?.name === 'ResetPassword') {
      return true;
    }

    // Also check browser URL for reset password pattern
    if (Platform.OS === 'web' && typeof window !== 'undefined') {
      const urlPath = window.location.pathname;
      if (urlPath.startsWith('/reset-password/')) {
        return true;
      }
    }

    return false;
  };

  // Check if current route is support route
  const checkSupportRoute = () => {
    if (currentRoute?.name === 'Support') {
      return true;
    }

    // Also check browser URL for support pattern
    if (Platform.OS === 'web' && typeof window !== 'undefined') {
      const urlPath = window.location.pathname;
      if (urlPath === '/support') {
        return true;
      }
    }

    return false;
  };
  
  const isInvitationRoute = checkInvitationRoute();
  const isForgotPasswordRoute = checkForgotPasswordRoute();
  const isResetPasswordRoute = checkResetPasswordRoute();
  const isSupportRoute = checkSupportRoute();
  
  // Extract token from URL if needed
  const getTokenFromUrl = (): string | undefined => {
    if (typeof currentRoute?.params?.token === 'string') {
      return currentRoute.params.token;
    }
    
    if (Platform.OS === 'web' && typeof window !== 'undefined') {
      const urlPath = window.location.pathname;
      const inviteMatch = urlPath.match(/\/invite\/(.+)/);
      if (inviteMatch) {
        return inviteMatch[1] || undefined;
      }
      const resetMatch = urlPath.match(/\/reset-password\/(.+)/);
      if (resetMatch) {
        return resetMatch[1] || undefined;
      }
    }
    
    return undefined;
  };
  
  const token = getTokenFromUrl();
  

  const initialMetrics = Platform.OS === 'web' ? {
    frame: { x: 0, y: 0, width: 0, height: 0 },
    insets: { top: 0, left: 0, right: 0, bottom: 0 }
  } : undefined;

  return (
    <SafeAreaProvider initialMetrics={initialMetrics}>
      <View style={{ flex: 1, height: '100%' }}>
        <NavigationContainer<WebRootParamList>
          theme={webTheme}
          onStateChange={(state) => {
            const route = state?.routes[state.index ?? 0] as TrackedRoute | undefined;
            setCurrentRoute(route ?? null);
          }}
          onReady={() => {
          }}
          linking={{
            enabled: true,
            prefixes: [
              'http://localhost:8081',
              'http://127.0.0.1:8081', 
              'http://exhale-web-simple-dev-800097198147.s3-website-us-east-1.amazonaws.com',
              'https://d9cppm2trntn1.cloudfront.net',
            ],
            getInitialURL: async () => {
              // Parse the current URL to determine initial route
              if (Platform.OS === 'web' && typeof window !== 'undefined') {
                const url = window.location.href;
                return url;
              }
              return null;
            },
            subscribe: (listener) => {
              if (Platform.OS === 'web' && typeof window !== 'undefined') {
                const handlePopState = () => {
                  listener(window.location.href);
                };
                window.addEventListener('popstate', handlePopState);
                return () => window.removeEventListener('popstate', handlePopState);
              }
              return () => {};
            },
            config: {
              initialRouteName: 'Home',
              screens: {
                Home: {
                  path: '',
                  exact: true,
                },
                Reviews: {
                  path: 'reviews',
                  screens: {
                    ReviewList: '',
                    ReviewDetail: {
                      path: 'detail/:reviewId',
                      parse: {
                        reviewId: (reviewId: string) => parseInt(reviewId, 10),
                      },
                    },
                    ItemDetail: {
                      path: 'detail/:reviewId/item/:itemId',
                      parse: {
                        reviewId: (reviewId: string) => parseInt(reviewId, 10),
                        itemId: (itemId: string) => parseInt(itemId, 10),
                      },
                    },
                  },
                },
                Results: {
                  path: 'results',
                  screens: {
                    ResultsList: '',
                    ResultDetail: {
                      path: 'detail/:reviewId',
                      parse: {
                        reviewId: (reviewId: string) => parseInt(reviewId, 10),
                      },
                    },
                    ItemDetail: {
                      path: 'detail/:reviewId/edit/:itemId',
                      parse: {
                        reviewId: (reviewId: string) => parseInt(reviewId, 10),
                        itemId: (itemId: string) => parseInt(itemId, 10),
                      },
                    },
                  },
                },
                Profile: 'profile',
                Admin: 'admin',
                InvitationRegister: {
                  path: 'invite/:token',
                  parse: {
                    token: (token: string) => token,
                  },
                },
                ForgotPassword: 'forgot-password',
                ResetPassword: {
                  path: 'reset-password/:token',
                  parse: {
                    token: (token: string) => token,
                  },
                },
                Support: 'support',
              },
            },
          }}
        >
          {(() => {

            if (isInvitationRoute) {
              return <InvitationRegisterScreen token={token} />;
            } else if (isForgotPasswordRoute) {
              return <ForgotPasswordScreen />;
            } else if (isResetPasswordRoute) {
              return <ResetPasswordScreen token={token} />;
            } else if (isSupportRoute) {
              return <SupportScreen />;
            } else if (isAuthenticated) {
              return <AppNavigator />;
            } else {
              return <LoginScreen />;
            }
          })()}
        </NavigationContainer>
      </View>
    </SafeAreaProvider>
  );
};

const ExhaleApp = () => {
  useEffect(() => {
    // No longer needed - root is always visible without password protection
  }, []);

  return (
    <Provider store={store}>
      <PersistGate loading={null} persistor={persistor}>
        <PaperProvider
          theme={theme}
          settings={Platform.OS === 'web' ? {
            icon: (props) => <MdiIconWeb {...props} />
          } : undefined}
        >
          <ReviewSearch>
            <AppContent />
          </ReviewSearch>
        </PaperProvider>
      </PersistGate>
    </Provider>
  );
};

export default ExhaleApp;
